/**
 * Adapter de red para Web
 *
 * Implementa NetworkPort usando Navigator.onLine y eventos del navegador.
 */

import type { NetworkPort, NetworkStatus, ConnectionType } from '@pepi/core'

/**
 * Implementación de NetworkPort para Web
 */
export class WebNetworkAdapter implements NetworkPort {
  private listeners: Set<(status: NetworkStatus) => void> = new Set()

  constructor() {
    // Configurar listeners globales
    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    if (typeof window === 'undefined') return

    window.addEventListener('online', () => {
      this.notifyListeners()
    })

    window.addEventListener('offline', () => {
      this.notifyListeners()
    })

    // Listener para cambios de conexión si está disponible
    if ('connection' in navigator) {
      const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection
      connection?.addEventListener('change', () => {
        this.notifyListeners()
      })
    }
  }

  private notifyListeners(): void {
    this.getStatus().then(status => {
      this.listeners.forEach(callback => callback(status))
    })
  }

  async getStatus(): Promise<NetworkStatus> {
    const connected = typeof navigator !== 'undefined' ? navigator.onLine : true
    const connectionType = this.getConnectionType()

    return {
      connected,
      connectionType
    }
  }

  private getConnectionType(): ConnectionType {
    if (typeof navigator === 'undefined') return 'unknown'

    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection
    if (!connection) return connected() ? 'unknown' : 'none'

    const type = connection.effectiveType || connection.type
    switch (type) {
      case 'wifi':
        return 'wifi'
      case 'cellular':
      case '4g':
      case '3g':
      case '2g':
        return 'cellular'
      case 'ethernet':
        return 'ethernet'
      case 'none':
        return 'none'
      default:
        return 'unknown'
    }

    function connected(): boolean {
      return typeof navigator !== 'undefined' ? navigator.onLine : true
    }
  }

  async isOnline(): Promise<boolean> {
    const status = await this.getStatus()
    return status.connected
  }

  onStatusChange(callback: (status: NetworkStatus) => void): () => void {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }
}

/**
 * Interface para Network Information API
 */
interface NetworkInformation extends EventTarget {
  effectiveType?: string
  type?: string
  addEventListener(type: 'change', listener: () => void): void
}

/**
 * Crea una instancia del adapter de red para web
 */
export function createWebNetworkAdapter(): NetworkPort {
  return new WebNetworkAdapter()
}
